/**
 * Material Taxonomy Validation and Utilities
 * Contains Yup schemas and helper functions
 */

import * as Yup from 'yup'
import { transformNullToString } from '../../../../shared/transformStringValue'
import { transformNumberValueAllowZero } from '../../../../shared/transformNumberValue'
import { IFunctionalUnitCreateDTO, IMaterialProcessingActivityOption } from './types'

// Keep malformed input invalid (NaN), rather than interpreting it as an intentional clear.
export function normalizeProcessingActivityId(value: unknown): number | null {
  if (value == null || (typeof value === 'string' && value.trim() === '')) return null
  if (typeof value !== 'number' && typeof value !== 'string') return NaN
  const id = Number(value)
  return id === -1 ? null : id
}

export function formatProcessingActivityLabel(pa: IMaterialProcessingActivityOption): string {
  const processFlow = pa.processFlow ?? ''
  if (!pa.code) return processFlow
  return processFlow ? `${pa.code} - ${processFlow}` : pa.code
}

const processingActivityIdSchema = Yup.number()
  .nullable()
  .default(null)
  .transform((_value, original) => normalizeProcessingActivityId(original))
  .typeError('Processing activity must be an integer ID')
  .test(
    'processing-activity-id',
    'Processing activity must be an integer ID',
    value => value == null || (Number.isFinite(value) && Number.isInteger(value))
  )

function serializeProcessingActivityId(value: unknown): number | null {
  const id = normalizeProcessingActivityId(value)
  if (id !== null && (!Number.isFinite(id) || !Number.isInteger(id))) {
    throw new TypeError('Processing activity must be an integer ID')
  }
  return id
}

/**
 * Validation schema for Functional Unit form
 * Mirrors Special Ingredients validation with Material Taxonomy specifics
 */
export const functionalUnitFormSchema = Yup.object().shape({
  disposalIncinerationProcessingActivityId: processingActivityIdSchema,
  disposalLandfillProcessingActivityId: processingActivityIdSchema,
  name: Yup.string()
    .transform(transformNullToString)
    .required('Name is required')
    .max(256, 'Name maximum length is 256 characters'),

  categoryId: Yup.number()
    .typeError('Category is required')
    .min(1, 'Please select a category')
    .required('Category is required'),

  transportCondition: Yup.number()
    .nullable()
    .transform(transformNumberValueAllowZero)
    .typeError('Field accepts numbers only'),
})

/**
 * Transform form values before submission
 * Handles null/undefined conversions and type coercion
 */
export const transformFunctionalUnitValues = (values: any): IFunctionalUnitCreateDTO => {
  const categoryId = Number(values.categoryId) || 0
  const transportCondition =
    values.transportCondition !== null && values.transportCondition !== undefined
      ? Number(values.transportCondition)
      : 0

  return {
    alias: values.alias?.trim() || undefined,
    disposalIncinerationProcessingActivityId: serializeProcessingActivityId(
      values.disposalIncinerationProcessingActivityId
    ),
    disposalLandfillProcessingActivityId: serializeProcessingActivityId(
      values.disposalLandfillProcessingActivityId
    ),
    name: values.name?.trim() || '',
    categoryId,
    transportCondition,
  }
}

/**
 * Sort dropdown array by name alphabetically and convert to options format
 * Maintains consistency with existing module patterns
 */
export const sortDropdown = <T extends { id: number; name: string; description?: string }>(
  arr: T[]
): Array<{ label: string; value: number }> => {
  const sorted = [...arr].sort((a, b) => a.name.localeCompare(b.name))
  return sorted.map(item => ({
    label: item.description || item.name,
    value: item.id,
  }))
}

/**
 * Get state label from state enum value
 */
export const getStateLabel = (state: number): string => {
  return state === 0 ? 'Draft' : state === 1 ? 'Published' : 'Unknown'
}

/**
 * Get transport condition label from enum value
 */
export const getTransportConditionLabel = (condition: number | null): string => {
  if (condition === null) return 'N/A'
  switch (condition) {
    case 0:
      return 'Ambient'
    case 1:
      return 'Chilled'
    case 2:
      return 'Frozen'
    default:
      return 'Unknown'
  }
}

/**
 * Format date string for display
 */
export const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'N/A'
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateString
  }
}
